import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { 
  Search, 
  Filter, 
  SortAsc, 
  SortDesc, 
  Eye, 
  Mail, 
  Building, 
  Calendar,
  Star,
  Phone,
  Users,
  Archive,
  RotateCcw,
  Trash2,
  X,
  Target,
  ChevronLeft,
  ChevronRight,
  Download,
  Check
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useRBACData } from '../hooks/useRBACData';
import { useRBAC } from '../contexts/RBACContext';
import { useLeadsDB } from '../hooks/useLeadsDB';
import { LEAD_STATUS, supabase } from '../config/supabase';

const Leads = ({ onViewLead, campaignFilter, onClearCampaignFilter, onPageChange }) => {
  // Use RBAC-filtered leads for role-based access
  const { leads } = useRBACData();
  const { canDelete, isAdminOrHigher, assignedCampaigns } = useRBAC();
  const { loading, updateLeadStatus, archiveLeads, moveToCallList, restoreLeads, deleteLeads, addToCallList, getLeadEmailCount } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState({ min: '', max: '' });
  const [campaignDropdownFilter, setCampaignDropdownFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage] = useState(10);

  // Advanced Campaign Filtering State
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [advancedFilterApplied, setAdvancedFilterApplied] = useState(false);
  const [showCampaignSelector, setShowCampaignSelector] = useState(false);

  // Clear status filter when toggling between archived and active views
  useEffect(() => {
    setStatusFilter('');
    setSelectedLeads([]);
  }, [showArchived]);

  // Get unique campaigns for filter dropdown (uses RBAC-filtered leads from DataContext)
  const campaigns = useMemo(() => {
    return [...new Set(leads.map(lead => lead.campaign_name).filter(Boolean))];
  }, [leads]);

  // ── DB-level leads (all filtering / sorting / pagination at DB level) ──
  const {
    leads: paginatedLeads,
    total: totalLeads,
    totalPages,
    loading: loadingLeads,
    refetch: refetchLeads,
  } = useLeadsDB({
    searchTerm,
    statusFilter,
    showArchived,
    scoreMin:         scoreFilter.min,
    scoreMax:         scoreFilter.max,
    campaignFilter,
    campaignDropdown: campaignDropdownFilter,
    advCampaigns:     selectedCampaigns,
    advFilterApplied: advancedFilterApplied,
    sortBy,
    sortOrder,
    page:     currentPage,
    pageSize: leadsPerPage,
  });

  const startIndex = (currentPage - 1) * leadsPerPage;
  const endIndex   = startIndex + leadsPerPage;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, scoreFilter, campaignFilter, campaignDropdownFilter, showArchived, advancedFilterApplied, sortBy, sortOrder]);

  // Handle campaign selection
  const handleCampaignToggle = (campaignName) => {
    setSelectedCampaigns(prev => {
      if (prev.includes(campaignName)) {
        return prev.filter(c => c !== campaignName);
      } else {
        return [...prev, campaignName];
      }
    });
  };

  // Apply advanced filter
  const handleApplyAdvancedFilter = () => {
    if (selectedCampaigns.length === 0) {
      logger.warn('Please select at least one campaign before filtering.');
      return;
    }
    setAdvancedFilterApplied(true);
    setShowCampaignSelector(false);
    setActionMessage(`Advanced filter applied: ${selectedCampaigns.length} campaign(s) selected`);
    setTimeout(() => setActionMessage(''), 3000);
  };

  // Clear advanced filter
  const handleClearAdvancedFilter = () => {
    setAdvancedFilterApplied(false);
    setSelectedCampaigns([]);
    setActionMessage('Advanced filter cleared');
    setTimeout(() => setActionMessage(''), 3000);
  };

  // Export to Excel — fetches all filtered leads from DB (not just current page)
  const handleExportToExcel = async () => {
    try {
      const rbacCampaigns = isAdminOrHigher() ? null : (assignedCampaigns?.length ? assignedCampaigns : []);
      const { data: result } = await supabase.rpc('get_leads_page', {
        p_campaigns:         rbacCampaigns,
        p_search:            searchTerm            ? searchTerm.trim()              : null,
        p_status_filter:     statusFilter          ? statusFilter                   : null,
        p_show_archived:     showArchived,
        p_score_min:         scoreFilter.min       ? parseInt(scoreFilter.min, 10)  : null,
        p_score_max:         scoreFilter.max       ? parseInt(scoreFilter.max, 10)  : null,
        p_campaign_filter:   (!advancedFilterApplied && campaignFilter)            ? campaignFilter            : null,
        p_campaign_dropdown: (!advancedFilterApplied && !campaignFilter && campaignDropdownFilter) ? campaignDropdownFilter : null,
        p_adv_campaigns:     (advancedFilterApplied && selectedCampaigns?.length) ? selectedCampaigns : null,
        p_sort_by:           sortBy     || 'created_at',
        p_sort_order:        sortOrder  || 'desc',
        p_page:              1,
        p_page_size:         10000,
      });

      const allLeads = result?.leads || [];

      if (allLeads.length === 0) {
        logger.warn('No leads to export. Please adjust your filters.');
        return;
      }

      const headers = ['First Name', 'Email', 'Company', 'Phone', 'Website'];
      const csvContent = [
        headers.join(','),
        ...allLeads.map(lead => {
          const row = {
            'First Name': lead.first_name || '',
            'Email':      lead.email      || '',
            'Company':    lead.company    || '',
            'Phone':      lead.phone      || '',
            'Website':    lead.website    || '',
          };
          return headers.map(h => {
            const v = row[h] || '';
            return v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v;
          }).join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `filtered_leads_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setActionMessage(`Successfully exported ${allLeads.length} lead(s) to Excel`);
      setTimeout(() => setActionMessage(''), 3000);
    } catch (error) {
      logger.error('Error exporting to Excel:', error);
      setActionMessage('Error exporting leads. Please try again.');
      setTimeout(() => setActionMessage(''), 3000);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === paginatedLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(paginatedLeads.map(lead => lead.id));
    }
  };

  // Bulk action handlers
  const handleMoveToCallList = async () => {
    if (selectedLeads.length === 0) return;
    
    setBulkActionLoading(true);
    setActionMessage('');
    
    try {
      const result = await moveToCallList(selectedLeads);
      if (result.success) {
        setActionMessage(`Successfully moved ${result.updatedCount} lead${result.updatedCount > 1 ? 's' : ''} to call list`);
        setSelectedLeads([]);
        refetchLeads();
        setTimeout(() => setActionMessage(''), 3000);
      } else {
        setActionMessage(`Error: ${result.error}`);
      }
    } catch (error) {
      logger.error('Error moving leads to call list:', error);
      setActionMessage(`Error: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleCallLead = async (leadId) => {
    try {
      // Add lead to call list first
      await addToCallList(leadId);
      // Navigate to call list page
      onPageChange('calllist');
    } catch (error) {
      logger.error('Error adding lead to call list:', error);
    }
  };

  const handleArchiveLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    setBulkActionLoading(true);
    setActionMessage('');
    
    try {
      const result = await archiveLeads(selectedLeads);
      if (result.success) {
        setActionMessage(`Successfully archived ${result.updatedCount} lead${result.updatedCount > 1 ? 's' : ''}`);
        setSelectedLeads([]);
        refetchLeads();
        setTimeout(() => setActionMessage(''), 3000);
      } else {
        setActionMessage(`Error: ${result.error}`);
      }
    } catch (error) {
      logger.error('Error archiving leads:', error);
      setActionMessage(`Error: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleRestoreLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    setBulkActionLoading(true);
    setActionMessage('');
    
    try {
      const result = await restoreLeads(selectedLeads);
      if (result.success) {
        setActionMessage(`Successfully restored ${result.updatedCount} lead${result.updatedCount > 1 ? 's' : ''}`);
        setSelectedLeads([]);
        refetchLeads();
        setTimeout(() => setActionMessage(''), 3000);
      } else {
        setActionMessage(`Error: ${result.error}`);
      }
    } catch (error) {
      logger.error('Error restoring leads:', error);
      setActionMessage(`Error: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDeleteLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    // Confirm deletion
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete ${selectedLeads.length} lead${selectedLeads.length > 1 ? 's' : ''}? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    setBulkActionLoading(true);
    setActionMessage('');
    
    try {
      const result = await deleteLeads(selectedLeads);
      if (result.success) {
        setActionMessage(`Successfully deleted ${result.deletedCount} lead${result.deletedCount > 1 ? 's' : ''} permanently`);
        setSelectedLeads([]);
        refetchLeads();
        setTimeout(() => setActionMessage(''), 3000);
      } else {
        setActionMessage(`Error: ${result.error}`);
      }
    } catch (error) {
      logger.error('Error deleting leads:', error);
      setActionMessage(`Error: ${error.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      [LEAD_STATUS.NEW]: 'bg-blue-100 text-blue-800',
      [LEAD_STATUS.INTERESTED]: 'bg-green-100 text-green-800',
      [LEAD_STATUS.CALL_LIST]: 'bg-orange-100 text-orange-800',
      [LEAD_STATUS.ARCHIVED]: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (score) => {
    if (score >= 20) return 'text-red-600 font-bold';
    if (score >= 15) return 'text-orange-600 font-semibold';
    if (score >= 10) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-gray-900 hover:text-primary-600"
    >
      <span>{children}</span>
      {sortBy === field && (
        sortOrder === 'asc' ? 
          <SortAsc className="w-4 h-4" /> : 
          <SortDesc className="w-4 h-4" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col py-6 lg:py-8 space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {showArchived ? 'Archived Leads' : 'Leads'}
          </h1>
          <p className="text-gray-600 text-lg">
            {totalLeads} of {showArchived ? leads.filter(l => l.status === LEAD_STATUS.ARCHIVED).length : leads.filter(l => l.status !== LEAD_STATUS.ARCHIVED).length} {showArchived ? 'archived ' : ''}leads
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex items-center space-x-3"
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCampaignSelector(!showCampaignSelector)}
            className={`btn-secondary px-4 py-2 text-sm font-medium transition-all duration-300 ${
              advancedFilterApplied ? 'bg-green-100 text-green-700 border-green-300' : ''
            }`}
          >
            <Target className="w-4 h-4 mr-2" />
            Campaign Filter
          </motion.button>
          {advancedFilterApplied && totalLeads > 0 && (
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExportToExcel}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowArchived(!showArchived)}
            className={`btn-secondary px-4 py-2 text-sm font-medium transition-all duration-300 ${
              showArchived ? 'bg-orange-100 text-orange-700 border-orange-300' : ''
            }`}
          >
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Show Active' : 'Show Archived'}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary px-4 py-2 text-sm font-medium transition-all duration-300 ${
              showFilters ? 'bg-primary-100 text-primary-700 border-primary-300' : ''
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </motion.button>
        </motion.div>
      </div>

      {/* Advanced Campaign Filter Panel */}
      <AnimatePresence>
        {showCampaignSelector && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6"
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <Target className="w-5 h-5 mr-2 text-primary-600" />
                Select Campaigns for Advanced Filtering
              </h3>
              <p className="text-sm text-gray-600">
                Select one or more campaigns, then click "Apply Filter" to show only leads matching the filtering criteria.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 max-h-60 overflow-y-auto">
              {campaigns.length > 0 ? (
                campaigns.map(campaign => (
                  <motion.button
                    key={campaign}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCampaignToggle(campaign)}
                    className={`p-3 rounded-lg border-2 text-left transition-all duration-200 ${
                      selectedCampaigns.includes(campaign)
                        ? 'border-primary-500 bg-primary-50 text-primary-900'
                        : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{campaign}</span>
                      {selectedCampaigns.includes(campaign) && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </div>
                  </motion.button>
                ))
              ) : (
                <p className="text-sm text-gray-500 col-span-full">No campaigns available</p>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {selectedCampaigns.length} campaign(s) selected
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowCampaignSelector(false)}
                  className="btn-secondary px-4 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyAdvancedFilter}
                  disabled={selectedCampaigns.length === 0}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Advanced Filter Active Banner */}
      {advancedFilterApplied && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Filter className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-900">
                Advanced filter active: <span className="font-bold">{selectedCampaigns.join(', ')}</span>
              </p>
              <p className="text-xs text-green-600">
                {totalLeads} lead(s) match criteria (0 opens across all events, 0 valid replies)
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClearAdvancedFilter}
            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-lg transition-colors"
            title="Clear advanced filter"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </motion.div>
      )}

      {/* Campaign Filter Banner */}
      {!advancedFilterApplied && campaignFilter && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary-50 border border-primary-200 rounded-xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <Target className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary-900">
                Showing leads from campaign: <span className="font-bold">{campaignFilter}</span>
              </p>
              <p className="text-xs text-primary-600">
                {totalLeads} lead{totalLeads !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClearCampaignFilter}
            className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-100 rounded-lg transition-colors"
            title="Clear campaign filter"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </motion.div>
      )}

      {/* Search and Filters */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="space-y-6"
      >
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search leads by email, name, company, or campaign..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white placeholder-gray-400 text-gray-900 shadow-sm hover:shadow-md text-lg"
          />
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl shadow-soft border border-gray-200 p-6 lg:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input-field"
                  disabled={showArchived}
                >
                  <option value="">All Statuses</option>
                  {!showArchived && (
                    <>
                      <option value={LEAD_STATUS.NEW}>New</option>
                      <option value={LEAD_STATUS.INTERESTED}>Interested</option>
                      <option value={LEAD_STATUS.CALL_LIST}>Call List</option>
                    </>
                  )}
                  {showArchived && (
                    <option value={LEAD_STATUS.ARCHIVED}>Archived</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Score
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={scoreFilter.min}
                  onChange={(e) => setScoreFilter(prev => ({ ...prev, min: e.target.value }))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Score
                </label>
                <input
                  type="number"
                  placeholder="100"
                  value={scoreFilter.max}
                  onChange={(e) => setScoreFilter(prev => ({ ...prev, max: e.target.value }))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign
                </label>
                <select
                  value={campaignDropdownFilter}
                  onChange={(e) => setCampaignDropdownFilter(e.target.value)}
                  className="input-field"
                  disabled={!!campaignFilter}
                >
                  <option value="">All Campaigns</option>
                  {campaigns.map(campaign => (
                    <option key={campaign} value={campaign}>{campaign}</option>
                  ))}
                </select>
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Bulk Actions */}
      {selectedLeads.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-primary-700">
              {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              {showArchived ? (
                <>
                  <button 
                    onClick={handleRestoreLeads}
                    disabled={bulkActionLoading}
                    className="btn-secondary text-sm py-1 px-3 disabled:opacity-50 flex items-center space-x-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>{bulkActionLoading ? 'Processing...' : 'Restore'}</span>
                  </button>
                  {canDelete() && (
                    <button 
                      onClick={handleDeleteLeads}
                      disabled={bulkActionLoading}
                      className="bg-red-600 hover:bg-red-700 text-white text-sm py-1 px-3 rounded-lg disabled:opacity-50 flex items-center space-x-1 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>{bulkActionLoading ? 'Processing...' : 'Delete Permanently'}</span>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button 
                    onClick={handleMoveToCallList}
                    disabled={bulkActionLoading}
                    className="btn-secondary text-sm py-1 px-3 disabled:opacity-50 flex items-center space-x-1"
                  >
                    <Phone className="w-3 h-3" />
                    <span>{bulkActionLoading ? 'Processing...' : 'Add to Call List'}</span>
                  </button>
                  <button 
                    onClick={handleArchiveLeads}
                    disabled={bulkActionLoading}
                    className="btn-secondary text-sm py-1 px-3 disabled:opacity-50 flex items-center space-x-1"
                  >
                    <Archive className="w-3 h-3" />
                    <span>{bulkActionLoading ? 'Processing...' : 'Archive'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
          {actionMessage && (
            <div className={`text-sm mt-2 ${actionMessage.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
              {actionMessage}
            </div>
          )}
        </motion.div>
      )}

      {/* Table */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="flex-1 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col min-h-0"
      >
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === paginatedLeads.length && paginatedLeads.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton field="email">Contact Info</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton field="company">Company</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton field="score">Score</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton field="status">Status</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton field="campaign_name">Source</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Emails Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton field="last_event_date">Last Activity</SortButton>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <AnimatePresence mode="wait">
                {paginatedLeads.map((lead, index) => (
                  <motion.tr
                    key={`${currentPage}-${lead.id}`}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ 
                      delay: index * 0.03,
                      duration: 0.4,
                      ease: "easeOut"
                    }}
                    onClick={(e) => {
                      // Prevent opening lead view when clicking on checkbox or action buttons
                      if (e.target.type === 'checkbox' || e.target.closest('button')) {
                        return;
                      }
                      onViewLead(lead);
                    }}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {lead.first_name ? lead.first_name.charAt(0).toUpperCase() : lead.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {lead.first_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {lead.email}
                          </div>
                          <div className="flex items-center mt-1 space-x-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Lead
                            </span>
                            {lead.score >= 80 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                High Priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {lead.company || 'No Company'}
                        </div>
                        <div className="text-sm text-gray-500">
                          Individual Contact
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1">
                        <Star className={`w-4 h-4 ${getScoreColor(lead.score)}`} />
                        <span className={`text-sm font-medium ${getScoreColor(lead.score)}`}>
                          {lead.score || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                        {lead.status?.replace('_', ' ') || 'New'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {lead.campaign_name || 'Direct'}
                        </div>
                        <div className="text-sm text-gray-500">
                          Lead Source
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-900">
                          {getLeadEmailCount(lead.email)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {getLeadEmailCount(lead.email) === 1 ? 'email' : 'emails'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {lead.last_event_date 
                            ? new Date(lead.last_event_date).toLocaleDateString()
                            : 'No activity'
                          }
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {lead.score >= 20 && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleCallLead(lead.id)}
                            className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Add to Call List & Go to Call List"
                          >
                            <Phone className="w-4 h-4" />
                          </motion.button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-200 shadow-sm"
          >
            <motion.div 
              className="flex items-center text-sm text-gray-700"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <span className="font-medium">
                Showing {startIndex + 1} to {Math.min(endIndex, totalLeads)} of {totalLeads} leads
              </span>
            </motion.div>
            <motion.div 
              className="flex items-center space-x-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <motion.button
                whileHover={{ scale: 1.1, backgroundColor: "rgb(239 246 255)" }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`p-2.5 rounded-xl transition-all duration-200 ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50 shadow-sm hover:shadow-md'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </motion.button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  
                  return (
                    <motion.button
                      key={pageNum}
                      whileHover={{ 
                        scale: 1.1, 
                        y: -2,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setCurrentPage(pageNum)}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg transform scale-105'
                          : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50 shadow-sm hover:shadow-md bg-white border border-gray-200'
                      }`}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.1, backgroundColor: "rgb(239 246 255)" }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-2.5 rounded-xl transition-all duration-200 ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50 shadow-sm hover:shadow-md'
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </motion.div>
          </motion.div>
        )}

        {totalLeads === 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No leads found</h3>
            <p className="text-gray-500 text-lg max-w-md mx-auto">
              {searchTerm || statusFilter || campaignFilter || scoreFilter.min || scoreFilter.max
                ? 'Try adjusting your filters to see more results.'
                : 'Your leads will appear here once you start receiving data.'}
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Leads;